import {Timestamp} from '@angular/fire/firestore';

export const BARCODE_SCANNER_USAGE_UPDATED_AT_FIELD = 'updatedAt';
export const BARCODE_SCANNER_SAST_TIME_ZONE = 'Africa/Johannesburg';

export type BarcodeScannerUsageWrite = 'merge' | 'overwrite' | 'blocked';

/**
 * SAST calendar day for an instant, as YYYY-MM-DD. SAST is Africa/Johannesburg (UTC+2, no DST).
 *
 * @param instantMs - Unix epoch milliseconds.
 * @returns The SAST day key.
 */
export function sastDayKey(instantMs: number): string {
  return new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: BARCODE_SCANNER_SAST_TIME_ZONE,
    year: 'numeric',
  }).format(new Date(instantMs));
}

/**
 * Reads a Firestore timestamp field as epoch milliseconds.
 *
 * @param value - Field value from the usage document.
 * @returns Epoch milliseconds, or null when the value is not a timestamp.
 */
export function readUsageTimestampMillis(value: unknown): number | null {
  if (value instanceof Timestamp) {
    return value.toMillis();
  }
  if (typeof value === 'object' && value !== null && 'toMillis' in value && typeof (value as {toMillis: unknown}).toMillis === 'function') {
    const millis = (value as { toMillis(): number }).toMillis();
    return typeof millis === 'number' ? millis : null;
  }
  return null;
}

/**
 * Email keys already recorded on the usage document. `updatedAt` is not a user.
 *
 * @param data - Usage document fields.
 * @returns Distinct user emails.
 */
export function usageUserEmails(data: Record<string, unknown> | undefined): string[] {
  if (!data) {
    return [];
  }
  return Object.keys(data).filter((key: string) => key !== BARCODE_SCANNER_USAGE_UPDATED_AT_FIELD);
}

/**
 * Decides whether a same-day merge, a new-day overwrite, or a block is required.
 * The caller supplies both instants. A wrong device clock can make this choice differ from
 * Cloud rules; those rules reject the mismatched write so the user is not stored on the wrong day.
 *
 * @param data - Current usage document, or undefined when it does not exist.
 * @param email - Signed-in user's email.
 * @param nowMs - Instant used as "now" when comparing SAST days.
 * @param userCap - Maximum distinct users for one SAST day.
 * @returns The write the client should attempt.
 */
export function decideBarcodeScannerUsageWrite(
  data: Record<string, unknown> | undefined,
  email: string,
  nowMs: number,
  userCap: number,
): BarcodeScannerUsageWrite {
  const updatedAtMs = readUsageTimestampMillis(data?.[BARCODE_SCANNER_USAGE_UPDATED_AT_FIELD]);
  const isCurrentSastDay = updatedAtMs !== null && sastDayKey(updatedAtMs) === sastDayKey(nowMs);
  if (!isCurrentSastDay) {
    return 'overwrite';
  }

  const emails = usageUserEmails(data);
  if (emails.includes(email) || emails.length < userCap) {
    return 'merge';
  }
  return 'blocked';
}
